import { t } from 'i18next';
import { ensureString } from './index';
import type {
  AnamnesisDisplayType,
  AnamnesisSummaryContent,
  AnamnesisSummaryItem,
  AnamnesisSummarySection,
  AnamnesisRecord,
  RawAnamnesisRecord,
  VisitReasonChip,
  VisitReasonColoredText,
  VisitReasonContent,
} from 'types/anamnesis';

export const PATIENT_NAME_NOT_SPECIFIED = 'Nicht angegeben';
export const ANAMNESIS_SECTION_SEPARATOR = ' · ';

function normalizeVisitReason(rawVisitReason?: VisitReasonContent): VisitReasonContent | undefined {
  if (!rawVisitReason) {
    return undefined;
  }

  const chips: VisitReasonChip[] = Array.isArray(rawVisitReason.chips)
    ? rawVisitReason.chips
        .map(({ text, color }) => ({
          text: ensureString(text).trim(),
          color: ensureString(color),
        }))
        .filter(({ text }) => text.length > 0)
    : [];

  const colored: VisitReasonColoredText[] = Array.isArray(rawVisitReason.colored)
    ? rawVisitReason.colored
        .map(({ text, color }) => ({
          text: ensureString(text).trim(),
          color: ensureString(color),
        }))
        .filter(({ text }) => text.length > 0)
    : [];

  return {
    id: ensureString(rawVisitReason.id),
    text: ensureString(rawVisitReason.text).trim(),
    chips,
    colored,
    date: ensureString(rawVisitReason.date),
    seen: rawVisitReason.seen ?? false,
  };
}

function normalizeDisplayType(value: unknown): AnamnesisDisplayType {
  return value === 'red' ? 'red' : 'default';
}

function normalizeSummaryItem(raw: Partial<AnamnesisSummaryItem>): AnamnesisSummaryItem {
  return {
    text: ensureString(raw.text),
    displayType: normalizeDisplayType(raw.displayType),
    attachedToPrevious: Boolean(raw.attachedToPrevious),
  };
}

function normalizeSummarySection(raw: Partial<AnamnesisSummarySection>): AnamnesisSummarySection {
  const items = Array.isArray(raw.items) ? raw.items.map(normalizeSummaryItem) : [];
  return {
    heading: ensureString(raw.heading),
    items,
  };
}

function normalizeSummary(raw?: AnamnesisSummaryContent): AnamnesisSummaryContent | undefined {
  if (!raw) return undefined;

  const sections = Array.isArray(raw.sections)
    ? raw.sections.map(normalizeSummarySection).filter((section) => section.items.length > 0)
    : [];

  return {
    id: ensureString(raw.id),
    sections,
    hasAdditionalFiles: Boolean(raw.hasAdditionalFiles),
    date: ensureString(raw.date),
    seen: raw.seen ?? false,
  };
}

export function toAnamnesisRecord(raw: RawAnamnesisRecord): AnamnesisRecord {
  const medatixxPatientId = ensureString(raw.medatixxId);
  const birthdate = ensureString(raw.birthdate);
  const patientName = ensureString(raw.fullname) || PATIENT_NAME_NOT_SPECIFIED;

  return {
    id: ensureString(raw.id) || medatixxPatientId || '',
    patientName,
    medatixxPatientId,
    summary: normalizeSummary(raw.summary),
    birthdate,
    visitReason: normalizeVisitReason(raw?.visitReason),
  };
}

export function renderAnamnesisSummaryText(
  summary: AnamnesisSummaryContent | null | undefined
): string {
  if (!summary) return '';

  const parts: string[] = [];
  summary.sections.forEach((section) => {
    let rendered = '';
    section.items.forEach((item, index) => {
      if (!item.text) return;
      if (index === 0) {
        rendered = item.text;
      } else if (item.attachedToPrevious) {
        rendered = `${rendered} ${item.text}`;
      } else {
        rendered = `${rendered}${ANAMNESIS_SECTION_SEPARATOR}${item.text}`;
      }
    });
    if (!rendered) return;
    parts.push(section.heading ? `${section.heading}: ${rendered}` : rendered);
  });

  let text = parts.join(' | ');
  if (summary.hasAdditionalFiles) {
    const notice = t('recording.additionalFilesNotice');
    text = text ? `${text} ${notice}` : notice;
  }
  return text;
}
